import React, { useState, useEffect } from "react";
import DatePickerComponent from "../datepicker/DatePicker";
import Modal from "../modal/Modal";
import PrimaryButton from "../buttons/PrimaryButton";
import "../../styles/cards/card.css";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import axios from "axios";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";
import Cookies from "js-cookie";
import "react-loading-skeleton/dist/skeleton.css";
import "../../styles/recordedData/recordedData.css";
import SingleSupervisorTable from "../sectorsTable/SingleSupervisorTable";

const SingleSiteRecordedData = ({ siteId, province, district, sector }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [doneAt, setDoneAt] = useState(new Date());
  const [availableBooks, setAvailableBooks] = useState([]);
  const [fetchingBooks, setFetchingBooks] = useState(false);
  const [uniqueBookCategories, setUniqueBookCategories] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("approved");
  const [tableActiveTab, setTableActiveTab] = useState("scanned-records");
  const [scannerOperators, setScannerOperators] = useState([]);
  const [moveToArchiveConfirmation, setMoveToArchiveConfirmation] =
    useState(false);
  const [formData, setFormData] = useState({
    category: "",
    bookNumber: "",
    type: "",
    count: "",
    yellowFormCount: "",
    assignedTo: "",
    createdAt: new Date().toISOString().split("T")[0],
  });
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [recordCounts, setRecordCounts] = useState({
    scannedRecords: 0,
    yellowForms: 0,
  });
  const role = Cookies.get("role");

  const handleRecordCountsChange = (counts) => {
    setRecordCounts(counts);
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    setFormData((prev) => ({
      ...prev,
      category: selectedCategory,
      type: "",
    }));

    const types = availableBooks
      .filter((book) => book.name === selectedCategory)
      .map((book) => book.bookType);
      console.log(`Types for ${selectedCategory}:`, types);
    setAvailableTypes(types);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    setFormData((prev) => ({
      category: activeTab === "approved" ? prev.category : "",
      bookNumber: "",
      type: activeTab === "approved" ? prev.type : "",
      count: activeTab === "approved" ? prev.count : "",
      yellowFormCount: activeTab === "all" ? prev.yellowFormCount : "",
      assignedTo: "",
      createdAt: new Date().toISOString().split("T")[0],
    }));
    setAvailableTypes([]);
    setError("");
  }, [activeTab]);

  // add new record
  const handleAddNewRecord = async () => {
    try {
      setSubmitting(true);
      setError("");

      if (!formData.category || !formData.count || !formData.bookNumber) {
        setError("Please fill in all required fields");
        return;
      }
      const token = Cookies.get("token");
      const requestData = {
        category: formData.category,
        count: formData.count,
        type: formData.type || null,
        bookNumber: formData.bookNumber,
        assignedTo: formData.assignedTo || null,
        createdAt: formData.createdAt,
      };
      const response = await axios.post(
        `${baseUrl}/api/scanned/${siteId}/scanned-records`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 201 || response.status === 200) {
        setShowModal(false);
        setConfirmationMessage("Record has been added successfully!");
        setShowConfirmationModal(true);
        setFormData({
          category: "",
          count: "",
          yellowFormCount: "",
          type: "",
          bookNumber: "",
          assignedTo: "",
          createdAt: new Date().toISOString().split("T")[0],
        });
        setError("");
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      if (error.response?.status === 409) {
      setError("A record with this book number already exists");
    } else {
      console.error("Error creating scanned record:", error);
      setError("Error creating scanned record");
    }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNewYellowForm = async () => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const requestData = {
        yellowFormCount: parseInt(formData.yellowFormCount),
        createdAt: formData.createdAt,
        assignedTo: formData.assignedTo || null,
      };
      const response = await axios.post(
        `${baseUrl}/api/yellow-forms/${siteId}/scanned`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 201 || response.status === 200) {
        setShowModal(false);
        setConfirmationMessage("Record has been added successfully!");
        setShowConfirmationModal(true);
        setFormData({
        category: "",
        count: "",
        yellowFormCount: "",
        type: "",
        bookNumber: "",
        assignedTo: "",
        createdAt: new Date().toISOString().split("T")[0],
      });
        setError("");
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setError(
          error.response?.data?.error ||
            "A yellow form entry has already been recorded for this operator at this site today"
        );
      } else {
        setError("Error creating yellow form");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchScannerOperators = async () => {
    try {
      const token = Cookies.get("token");
      const response = await axios.get(
        `${baseUrl}/api/users/scanner-operator/${siteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const operators = response.data.map((item) => item.scannerOperator.name);
      setScannerOperators(operators);
    } catch (error) {
      setError("Error fetching scanner operators");
    }
  };

  useEffect(() => {
    if (showModal && role === "SCANNER_ASSISTANT") {
      fetchScannerOperators();
    }
  }, [showModal, role]);

  const fetchBookData = async () => {
    try {
      setFetchingBooks(true);
      const token = Cookies.get("token");
      const response = await fetch(`${baseUrl}/api/books`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const filteredData = data.filter((book) => book.name !== "Yellow Forms");
      setAvailableBooks(filteredData);
      const categories = [...new Set(filteredData.map((book) => book.name))].map(
      (category) =>
        category === "Special_category" ? "Special Category" : category
    );
    setUniqueBookCategories(categories);

      const allTypes = [...new Set(filteredData.map((book) => book.bookType))];
      setAvailableTypes(allTypes);
    } catch (error) {
      setError("Error fetching book data: " + error.message);
    } finally {
      setFetchingBooks(false);
    }
  };

  useEffect(() => {
    if (showModal && activeTab === "approved") {
      fetchBookData();
    }
  }, [showModal, activeTab]);

  const capitalize = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const handleSubmitData = async (e) => {
    e.preventDefault();
    if (activeTab === "approved") {
      await handleAddNewRecord();
    } else {
      await handleAddNewYellowForm();
    }
  };

  const handleButtonClick = (e) => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      category: "",
      count: "",
      yellowFormCount: "",
      type: "",
      bookNumber: "",
      assignedTo: "",
      createdAt: new Date().toISOString().split("T")[0],
    });
    setError("");
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleMoveToArchive = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");

      let endpoint;
      if (tableActiveTab === "scanned-records") {
        endpoint = `${baseUrl}/api/scanned/scanned-records/archives/manual-move`;
      } else {
        endpoint = `${baseUrl}/api/yellow-forms/archives/scanned-yellow-forms/move`;
      }

      const response = await axios.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        setMoveToArchiveConfirmation(false);
        setConfirmationMessage(
          tableActiveTab === "scanned-records"
            ? "Scanned records have been submitted for approval successfully!"
            : "Yellow forms have been submitted for approval successfully!"
        );
        setShowConfirmationModal(true);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      setError(
        `Error submitting ${
          tableActiveTab === "scanned-records"
            ? "scanned records"
            : "yellow forms"
        } for approval`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    // Fix: Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    setFormData((prev) => ({
      ...prev,
      createdAt: formattedDate,
    }));
  };

  const handleMoveToArchiveConfirmation = () => {
    setMoveToArchiveConfirmation(true);
  };

  const handleCloseMoveToArchiveConfirmation = () => {
    setMoveToArchiveConfirmation(false);
  };

  return (
    <>
      <div className="recorded-data-content">
        <div className="search-and-upload">
          <div className="tabs">
            <div
              className={`tab ${
                tableActiveTab === "scanned-records" ? "active" : ""
              }`}
              onClick={() => setTableActiveTab("scanned-records")}
            >
              Scanned Records
            </div>
            <div
              className={`tab ${
                tableActiveTab === "yellow-forms" ? "active" : ""
              }`}
              onClick={() => setTableActiveTab("yellow-forms")}
            >
              Yellow Forms
            </div>
          </div>

          <div className="upload-buttons">
            {(tableActiveTab === "scanned-records" && recordCounts.scannedRecords > 0) ||
            (tableActiveTab === "yellow-forms" && recordCounts.yellowForms > 0) ? (
              <PrimaryButton
                text="Submit For Approval"
                icon="solar:export-broken"
                disabled={loading}
                onClick={handleMoveToArchiveConfirmation}
              />
            ) : null}
            <PrimaryButton
              text="Add New Book"
              icon="si:add-fill"
              onClick={handleButtonClick}
            />
          </div>

          <Modal show={showModal} onClose={handleCloseModal} confirm={true}>
            <div className="title initialized">ADD SCANNED RECORDS</div>
            <div className="content">
              <div className="form-container new-records">
                <form action="">
                  <div className="scanned-books initialized">
                    <div className="address">
                      <div className="address-container">
                        <div className="address-text">Province: {province}</div>
                      </div>
                      <div className="address-container">
                        <div className="address-text">District: {district}</div>
                      </div>
                      {sector && (
                        <div className="address-container">
                          <div className="address-text">Sector: {sector}</div>
                        </div>
                      )}
                    </div>
                    <div className="tabs">
                      <div
                        className={`tab ${
                          activeTab === "approved" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("approved")}
                      >
                        CR Books
                      </div>
                      <div
                        className={`tab ${activeTab === "all" ? "active" : ""}`}
                        onClick={() => setActiveTab("all")}
                      >
                        Yellow Forms
                      </div>
                    </div>

                    <div className="data">
                      {activeTab === "approved" ? (
                        <>
                          <div className="books-container">
                            <div className="date-container">
                              <div className="date-heading">Date</div>
                              <DatePickerComponent
                                selectedDate={new Date(formData.createdAt)}
                                onDateChange={handleDateChange}
                              />
                            </div>
                            <div className="book-type-section">
                              <div className="number-section">
                                <div className="type-heading">Book Number</div>
                                <input
                                  className="input-field"
                                  placeholder="Enter Book Number"
                                  type="text"
                                  name="bookNumber"
                                  value={formData.bookNumber}
                                  onChange={handleInputChange}
                                  min="1"
                                />
                              </div>
                              <div className="category">
                                <div className="type-heading">
                                  Book Category
                                </div>
                                <div className="label">
                                  <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleCategoryChange}
                                    className="form-select"
                                  >
                                    <option value="" disabled>
                                      Select Book category
                                    </option>
                                    {uniqueBookCategories.map((category) => (
                                      <option key={category} value={category === "Special Category" ? "Special_category" : category}>
                                        {category}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>

                            <div className="book-category-section">
                              <div className="type-heading">Book Type</div>
                              <div className="book-category-options">
                                {availableTypes.map((type) => (
                                  <label key={type} className="radio-label">
                                    <input
                                      type="radio"
                                      name="type"
                                      value={type}
                                      checked={formData.type === type}
                                      onChange={handleInputChange}
                                      // disabled={!formData.category}
                                    />
                                    <span>{capitalize(type)}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div className="estimated-records-section">
                              <span className="type-heading">
                                Estimated Number of Records
                              </span>

                              <input
                                className="input-field"
                                placeholder="Number of Records"
                                type="number"
                                name="count"
                                value={formData.count}
                                onChange={handleInputChange}
                                min="1"
                              />
                            </div>

                            {role === "SCANNER_ASSISTANT" && (
                              <>
                                <div className="assigned-section">
                                  <span>Assigned To</span>
                                  <div className="label">
                                    <select
                                      name="assignedTo"
                                      value={formData.assignedTo}
                                      onChange={handleInputChange}
                                      className="form-select"
                                    >
                                      <option value="" disabled>
                                        Select Scanner Operator
                                      </option>
                                      {scannerOperators.map((operator) => (
                                        <option
                                          key={operator.id}
                                          value={operator}
                                        >
                                          {operator}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="yellow-forms-section">
                          <div className="date-container">
                            <div className="type-heading">Date</div>
                            <DatePickerComponent
                              selectedDate={new Date(formData.createdAt)}
                              onDateChange={handleDateChange}
                            />
                          </div>
                          <div className="estimated-records-section">
                            <span className="type-heading">
                              Estimated Number of Records
                            </span>
                            <input
                              className="input-field"
                              placeholder="Number of Records"
                              type="number"
                              name="yellowFormCount"
                              value={formData.yellowFormCount}
                              onChange={handleInputChange}
                              min="1"
                            />
                            {role === "SCANNER_ASSISTANT" && (
                              <>
                                <div className="assigned-section">
                                  <span>Assigned To</span>
                                  <div className="label">
                                    <select
                                      name="assignedTo"
                                      value={formData.assignedTo}
                                      onChange={handleInputChange}
                                      className="form-select"
                                    >
                                      <option value="" disabled>
                                        Select Scanner Operator
                                      </option>
                                      {scannerOperators.map((operator) => (
                                        <option
                                          key={operator.id}
                                          value={operator}
                                        >
                                          {operator}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="button-group">
                <PrimaryButton
                  text={
                    submitting ? (
                      <AiOutlineLoading3Quarters className="loader" />
                    ) : (
                      "Submit"
                    )
                  }
                  onClick={handleSubmitData}
                  disabled={submitting}
                />
                <PrimaryButton
                  text="Cancel"
                  onClick={handleCloseModal}
                  disabled={submitting}
                />
              </div>
            </div>


          </Modal>

          <Modal
            show={moveToArchiveConfirmation}
            onClose={handleCloseMoveToArchiveConfirmation}
          >
            <div className="content confirmation">
              <h2>Confirm Submission</h2>
              <div className="text">
                Are you sure you want to submit{" "}
                {tableActiveTab === "scanned-records" ? (
                  <>
                    {recordCounts.scannedRecords} scanned record
                    {recordCounts.scannedRecords !== 1 ? "s" : ""}
                  </>
                ) : (
                  <>
                    {recordCounts.yellowForms} yellow form
                    {recordCounts.yellowForms !== 1 ? "s" : ""}
                  </>
                )}{" "}
                for approval?
              </div>
              <div className="button-group">
                <PrimaryButton
                  text={
                    submitting ? (
                      <AiOutlineLoading3Quarters className="loader" />
                    ) : (
                      "Yes"
                    )
                  }
                  onClick={handleMoveToArchive}
                  disabled={loading}
                />
                <PrimaryButton
                  text="No"
                  onClick={handleCloseMoveToArchiveConfirmation}
                  disabled={loading}
                />
              </div>
            </div>
          </Modal>

          <Modal
            show={showConfirmationModal}
            onClose={handleCloseConfirmationModal}
          >
            <div className="content confirmation">
              <div className="icon-container">
                <img
                  src={confirmIcon}
                  alt="confirm"
                  width="57"
                  height="57"
                  style={{ color: "#007011" }}
                />
              </div>
              <div className="text">{confirmationMessage}</div>
            </div>
          </Modal>

          <div className="table-container">
            <div className="table-padding-wrapper">
              <SingleSupervisorTable
                siteId={siteId}
                activeTab={tableActiveTab}
                refreshTrigger={refreshTrigger}
                province={province}
                sector={sector}
                district={district}
                onRecordCountsChange={handleRecordCountsChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SingleSiteRecordedData;
